import type { TDocumentDefinitions } from 'pdfmake/interfaces.js';
import { formatDay } from '../../format.js';
import { TOKENS } from '../../tokens.js';
import type { DailyPlanData } from './types.js';

export type DailyPdfT = (key: string, options?: Record<string, unknown>) => string;

/**
 * "Export PDF" (BGM Quick Actions): günün planı, A4 yatay. Görevler saat sırasıyla; durum, sorumlu,
 * PMS bağı ve tamamlama notu. Gemide internet yok: yalnız gömülü yazı tipi (mimari belge §11.3).
 */
export function dailyPlanPdf(
  data: Pick<DailyPlanData, 'vessel' | 'day' | 'tasks' | 'summary'>,
  t: DailyPdfT,
  generatedAt: string,
): TDocumentDefinitions {
  const head = [
    t('daily.plan.columns.no'),
    t('daily.plan.columns.time'),
    t('daily.plan.columns.description'),
    t('daily.plan.columns.department'),
    t('daily.plan.columns.priority'),
    t('daily.plan.columns.responsible'),
    t('daily.plan.columns.status'),
    t('daily.details.notes'),
  ].map((text) => ({ text, bold: true, fillColor: TOKENS['table-head-bg'] }));
  const rows = data.tasks.map((task, index) => [
    String(index + 1),
    `${task.startTime}${task.endTime ? ` - ${task.endTime}` : ''}`,
    task.workOrder ? `${task.description}\n${task.workOrder.no}` : task.description,
    t(`daily.departments.${task.department}`),
    t(`daily.priorities.${task.priority}`),
    task.responsible,
    t(`daily.statuses.${task.overdue ? 'overdue' : task.status}`),
    task.completionNote ?? task.cancelReason ?? '',
  ]);
  const s = data.summary;
  return {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [32, 40, 32, 40],
    defaultStyle: { fontSize: 9 },
    info: { title: `${t('daily.pdf.title')} ${data.vessel.name} ${data.day}` },
    content: [
      { text: t('daily.pdf.title'), fontSize: 16, bold: true, margin: [0, 0, 0, 6] },
      {
        text: `${t('daily.pdf.vessel')}: ${data.vessel.name}${data.vessel.imo ? ` (IMO ${data.vessel.imo})` : ''}    ${t('daily.pdf.day')}: ${formatDay(data.day)}`,
        margin: [0, 0, 0, 4],
      },
      {
        text: [
          `${t('daily.summary.completed')}: ${s.completed}   `,
          `${t('daily.summary.inProgress')}: ${s.inProgress}   `,
          `${t('daily.summary.planned')}: ${s.planned}   `,
          `${t('daily.summary.overdue')}: ${s.overdue}`,
        ].join(''),
        margin: [0, 0, 0, 10],
      },
      {
        table: {
          headerRows: 1,
          widths: [18, 50, '*', 60, 44, 80, 56, 140],
          body: [head, ...rows],
        },
        layout: 'lightHorizontalLines',
      },
    ],
    footer: (page: number, pages: number) => ({
      columns: [
        {
          text: t('daily.pdf.generated', { at: generatedAt }),
          fontSize: 7,
          color: TOKENS['text-secondary'],
        },
        {
          text: `${page} / ${pages}`,
          alignment: 'right',
          fontSize: 7,
          color: TOKENS['text-secondary'],
        },
      ],
      margin: [32, 0, 32, 0],
    }),
  };
}

export function dailyPlanPdfFileName(vesselName: string, day: string): string {
  return `daily-works-plan-${vesselName.replace(/[^A-Za-z0-9]+/g, '-')}-${day}.pdf`;
}
